import { Component } from 'react'
import PropTypes from 'prop-types'
import { withRouter } from 'react-router-dom'

import { ROUTE } from '../config'
import { isOffline, onConnectivityChange } from '../services/offlineDetect'

/**
 * Returns whether the current path is the app homepage
 *
 * @param {string} pathname current router pathname
 * @returns {boolean}
 */
export function isHomeRoute(pathname) {
  return pathname === '/' || pathname === ''
}

/**
 * Returns whether the current path is a browse route (/browse or /browse/...)
 *
 * @param {string} pathname current router pathname
 * @returns {boolean}
 */
export function isBrowseRoute(pathname) {
  if (!pathname) return false

  const browse = ROUTE.BROWSE

  return (
    pathname === browse ||
    pathname.startsWith(`${browse}/`) ||
    pathname.endsWith(browse) ||
    pathname.includes(`${browse}/`)
  )
}

/**
 * Redirects to /offline when the app goes offline, unless the user is
 * already viewing browse content.
 */
class OfflineRouteRedirect extends Component {
  static propTypes = {
    history: PropTypes.shape({
      location: PropTypes.shape({
        pathname: PropTypes.string.isRequired,
      }).isRequired,
      replace: PropTypes.func.isRequired,
      listen: PropTypes.func.isRequired,
    }).isRequired,
  }

  /**
   * Redirects homepage routes to /offline when the app is offline
   *
   * @param {string} pathname current router pathname
   */
  redirectHomeIfOffline = (pathname) => {
    if (!isOffline()) return
    if (!isHomeRoute(pathname)) return
    if (pathname === ROUTE.OFFLINE) return

    this.props.history.replace(ROUTE.OFFLINE)
  }

  /**
   * Redirects non-browse routes to /offline when connectivity is lost
   *
   * @param {string} pathname current router pathname
   */
  redirectIfOfflineOutsideBrowse = (pathname) => {
    if (!isOffline()) return
    if (pathname === ROUTE.OFFLINE) return
    if (isBrowseRoute(pathname)) return

    this.props.history.replace(ROUTE.OFFLINE)
  }

  componentDidMount() {
    const { pathname } = this.props.history.location

    this.redirectHomeIfOffline(pathname)

    this.unsubscribeConnectivity = onConnectivityChange(online => {
      if (online) return
      this.redirectIfOfflineOutsideBrowse(this.props.history.location.pathname)
    })

    this.unsubscribeHistory = this.props.history.listen(({ pathname: nextPathname }) => {
      this.redirectHomeIfOffline(nextPathname)
    })
  }

  componentWillUnmount() {
    if (this.unsubscribeConnectivity) {
      this.unsubscribeConnectivity()
    }
    if (this.unsubscribeHistory) {
      this.unsubscribeHistory()
    }
  }

  render() {
    return null
  }
}

export default withRouter(OfflineRouteRedirect)
